import { CountdownStack } from './components/countdown-stack.js';
import { WeeklyCalendar } from './components/weekly-calendar.js';
import { FocusTimer } from './components/focus-timer.js';

/**
 * Dashboard UI Module
 * Handles UI initialization, rendering, and user interactions
 */
export class DashboardUI {
  constructor(dashboardCore) {
    this.dashboard = dashboardCore;
    this.clockInterval = null;

    // Header elements
    this.headerToggle = null;
    this.headerCountdownEl = null;
    this.headerCountdownLabelEl = null;
    this.headerCountdownValueEl = null;
    this.headerCountdownMetaEl = null;
    this.headerCountdownActionEl = null;

    // Calendar elements
    this.lastCompactCalendarDate = null;
  }

  async initialize() {
    // Initialize greeting and time display
    this.initializeWelcomeSection();

    // Initialize collapsible header
    this.initializeCollapsibleHeader();

    // Initialize countdown stack
    this.dashboard.core.modules.countdownStack = new CountdownStack({
      containerId: 'countdown-stack-container',
      data: this.dashboard.core.data,
      onRefresh: () => this.dashboard.updateHeaderCountdown(),
    });

    // Initialize other UI components
    this.initializeSecondaryComponents();
  }

  initializeWelcomeSection() {
    // Initialize greeting
    this.updateGreeting();

    // Initialize and start real-time clock
    this.updateClock();
    this.clockInterval = setInterval(() => this.updateClock(), 1000);

    // Render compact calendars in welcome card
    this.renderCompactCalendars();

    // Initialize collapsible header
    this.initializeCollapsibleHeader();
  }

  updateGreeting() {
    const greetingContainer = document.getElementById('greeting-container');
    if (greetingContainer) {
      const now = new Date();
      const hour = now.getHours();
      let greeting = 'Good morning';
      let subtitle = 'Ready to make today amazing?';

      if (hour >= 12 && hour < 17) {
        greeting = 'Good afternoon';
        subtitle = "How's your day going?";
      } else if (hour >= 17) {
        greeting = 'Good evening';
        subtitle = 'Time to wind down and reflect';
      }

      // Add energy-based encouragement for ADHD
      const encouragements = [
        "You've got this! 💪",
        'One step at a time',
        'Your pace is perfect',
        'Progress over perfection',
        'Be kind to yourself today',
        'Small wins count too',
      ];

      if (Math.random() > 0.5) {
        subtitle = encouragements[Math.floor(Math.random() * encouragements.length)];
      }

      const name = 'Marlie';

      greetingContainer.innerHTML = `
        <h2>${greeting}, ${name}!</h2>
        <p class="greeting-subtitle">${subtitle}</p>
      `;
    }
  }

  updateClock() {
    const now = new Date();

    // Update time
    const timeContainer = document.getElementById('current-time');
    if (timeContainer) {
      timeContainer.textContent = now.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    // Update date
    const dateContainer = document.getElementById('current-date');
    if (dateContainer) {
      dateContainer.textContent = now.toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }

    const todayKey = now.toDateString();
    if (this.lastCompactCalendarDate !== todayKey) {
      this.renderCompactCalendars();
    }

    this.dashboard.updateHeaderCountdown(now);

    if (now.getSeconds() % 30 === 0) {
      this.dashboard.refreshCountdownStack();
    }
  }

  renderCompactCalendars() {
    const today = new Date();
    this.renderCompactMonth(today);
    this.renderCompactWeek(today);
    this.lastCompactCalendarDate = today.toDateString();
  }

  renderCompactMonth(today) {
    const container = document.getElementById('monthly-calendar-compact');
    if (!container) {
      return;
    }

    const year = today.getFullYear();
    const month = today.getMonth();
    const monthName = today.toLocaleDateString([], { month: 'long' }).toUpperCase();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const weeks = [];
    let currentWeek = [];

    for (let date = new Date(startDate); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const dayOfMonth = date.getDate();
      const isToday = date.toDateString() === today.toDateString();
      const isCurrentMonth = date.getMonth() === month;

      currentWeek.push({
        day: dayOfMonth,
        isToday,
        isCurrentMonth,
        date: new Date(date),
      });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    // Flatten all days into a single array for the grid
    const allDays = weeks.flat();

    container.innerHTML = `
      <div class="compact-calendar__header">
        <h4 class="compact-calendar__title">${monthName}</h4>
      </div>
      <div class="compact-calendar__grid">
        ${allDays.map(day => `
          <div class="compact-calendar__day ${day.isToday ? 'is-today' : ''} ${!day.isCurrentMonth ? 'is-other-month' : ''}">
            ${day.day}
          </div>
        `).join('')}
      </div>
    `;
  }

  renderCompactWeek(today) {
    const container = document.getElementById('weekly-calendar-compact');
    if (!container) {
      return;
    }

    const startOfWeek = new Date(today);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

    const weekDays = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);
      const isToday = date.toDateString() === today.toDateString();
      return {
        day: date.getDate(),
        dayName: date.toLocaleDateString([], { weekday: 'short' }),
        isToday,
        date,
      };
    });

    container.innerHTML = `
      <div class="compact-week__header">
        <h4 class="compact-week__title">This Week</h4>
      </div>
      <div class="compact-week__grid">
        ${weekDays.map(day => `
          <div class="compact-week__day ${day.isToday ? 'is-today' : ''}">
            <div class="compact-week__day-name">${day.dayName}</div>
            <div class="compact-week__day-number">${day.day}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  initializeCollapsibleHeader() {
    this.headerToggle = document.getElementById('header-toggle');
    if (!this.headerToggle) {
      console.warn('[DashboardUI] Header toggle not found');
      return;
    }

    // Default to expanded when authenticated (only show header), collapsed otherwise
    const defaultCollapsed = !document.body.dataset.auth;
    const savedCollapsed = this.dashboard.core.data.get('headerCollapsed', defaultCollapsed);
    this.setHeaderCollapsed(Boolean(savedCollapsed), { persist: false, silent: true });

    this.headerToggle.addEventListener('click', () => {
      this.setHeaderCollapsed(!this.isHeaderCollapsed());
    });
  }

  setHeaderCollapsed(collapsed, options = {}) {
    const { persist = true, silent = false } = options;
    const targetState = Boolean(collapsed);

    const welcomeSection = document.getElementById('welcome-section');
    const previousState = welcomeSection?.classList.contains('collapsed');

    if (welcomeSection) {
      welcomeSection.classList.toggle('collapsed', targetState);
    }

    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.header = targetState ? 'collapsed' : 'expanded';
    }

    if (persist) {
      this.dashboard.core.data.set('headerCollapsed', targetState);
    }

    this.updateHeaderToggleState(targetState, { silent });

    if (previousState !== targetState) {
      document.dispatchEvent(
        new CustomEvent('header-state-changed', {
          detail: { collapsed: targetState },
        })
      );
    }

    return targetState;
  }

  isHeaderCollapsed() {
    const welcomeSection = document.getElementById('welcome-section');
    return welcomeSection?.classList.contains('collapsed') ?? false;
  }

  updateHeaderToggleState(collapsed, options = {}) {
    if (!this.headerToggle) {
      return;
    }

    const icon = this.headerToggle.querySelector('.toggle-icon');
    if (icon) {
      icon.textContent = collapsed ? '▼' : '▲';
    }

    this.headerToggle.dataset.state = collapsed ? 'collapsed' : 'expanded';
    this.headerToggle.setAttribute('aria-expanded', (!collapsed).toString());
    this.headerToggle.setAttribute('aria-label', collapsed ? 'Expand header' : 'Collapse header');
    this.headerToggle.title = collapsed ? 'Expand header' : 'Collapse header';

    if (this.dashboard.debug.headerToggle) {
      const computedTransform = collapsed ? 'translateY(-100%)' : 'translateY(0)';
      this.headerToggle.dataset.debug = `${collapsed ? 'collapsed' : 'expanded'} | ${computedTransform}`;
      this.headerToggle.classList.add('header-toggle--debug');
    } else {
      this.headerToggle.classList.remove('header-toggle--debug');
      delete this.headerToggle.dataset.debug;
    }

    if (!options.silent) {
      console.debug('[DashboardUI] Header toggle state changed', {
        collapsed,
        transform: collapsed ? 'translateY(-100%)' : 'translateY(0)',
        debug: this.dashboard.debug.headerToggle,
      });
    }
  }

  logHeaderToggleState() {
    const collapsed = this.isHeaderCollapsed();
    this.updateHeaderToggleState(collapsed, { silent: false });
  }

  initializeSecondaryComponents() {
    // Initialize focus timer in welcome panel
    this.dashboard.core.modules.focusTimer = new FocusTimer(
      document.getElementById('focus-timer-container'),
      this.dashboard.core.data
    );

    // Initialize weekly calendar component
    this.dashboard.core.modules.weeklyCalendar = new WeeklyCalendar(
      document.getElementById('weekly-calendar-container'),
      this.dashboard.core.data
    );
  }
}
